import { DesignSystemProvider, system } from '@hover/blueprint';
import React, { useContext } from 'react';

import { DoorGroupMenu } from 'src/modules/designStudio/components/DesignMenu/Menu/DoorGroupMenu';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { useShowRealProductSelectionUI } from 'src/modules/designStudio/components/DesignMenu/Menu/useShowRealProductSelectionUI';
import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { Menu as MenuConstants } from 'src/modules/designStudio/constants';
import { uiLayerContext } from 'src/modules/designStudio/providers';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';

import { RoofMenu as PCRoofMenu } from '../ProductCatalogMenu/RoofMenu/index';
import { TrimMenu as PCTrimMenu } from '../ProductCatalogMenu/TrimMenu/index';
import { WallMenu as PCWallMenu } from '../ProductCatalogMenu/WallMenu/index';

import { InteractiveRenderingRoofRegionsMenu } from './InteractiveRenderingRoofMenu';
import { InteractiveRenderingTrimRegionsMenu } from './InteractiveRenderingTrimMenu';
import { InteractiveRenderingWallRegionsMenu } from './InteractiveRenderingWallMenu';
import { TemplateMenu } from './TemplateMenu';
import { WindowMenu } from './WindowMenu';

const MenuComponentMap = {
  [MenuConstants.WINDOW]: WindowMenu,
  [MenuConstants.INTERACTIVE_RENDERING_WALL]:
    InteractiveRenderingWallRegionsMenu,
  [MenuConstants.INTERACTIVE_RENDERING_ROOF]:
    InteractiveRenderingRoofRegionsMenu,
  [MenuConstants.INTERACTIVE_RENDERING_TRIM]:
    InteractiveRenderingTrimRegionsMenu,
  [MenuConstants.TEMPLATE]: TemplateMenu,
  [MenuConstants.ACCENT]: null,
  [MenuConstants.DOOR]: DoorGroupMenu,
  [MenuConstants.VERSION]: null,
  [MenuConstants.RESET]: null,
  [MenuConstants.SHARE]: null,
  [MenuConstants.SHOW_DESIGN]: null,
  [MenuConstants.TRIM]: null,
  [MenuConstants.WALL]: null,
  [MenuConstants.ROOF]: null,
};

const MenusThatUseNext = [
  MenuConstants.TRIM,
  MenuConstants.WALL,
  MenuConstants.ROOF,
];

const RealProductsMenuComponentMap = {
  ...MenuComponentMap,
  [MenuConstants.TRIM]: PCTrimMenu,
  [MenuConstants.WALL]: PCWallMenu,
  [MenuConstants.ROOF]: PCRoofMenu,
};

export const Menu = ({
  menu,
  requestingOrgName,
  headerText,
  closeMenu,
}: {
  menu: MenuConstants | null;
  requestingOrgName?: string;
  headerText: string | null;
  closeMenu: () => void;
}) => {
  const showRealProductsSelectionUI = useShowRealProductSelectionUI();
  const { kind: uiKind } = useContext(uiLayerContext);

  const map = showRealProductsSelectionUI
    ? RealProductsMenuComponentMap
    : MenuComponentMap;

  const MenuComponent = menu && map[menu];

  if (!MenuComponent)
    return <MenuHeader closeMenu={closeMenu} headerText={headerText ?? ''} />;

  if (showRealProductsSelectionUI && MenusThatUseNext.some(m => m === menu)) {
    return (
      <DesignSystemProvider attach>
        <system.div height="100%">
          <SetIntercomVisibility visible={false} />
          <MenuComponent
            closeMenu={closeMenu}
            headerText={headerText}
            requestingOrgName={requestingOrgName}
          />
        </system.div>
      </DesignSystemProvider>
    );
  }

  /**
   * We have to add padding here because SlideSmartMenu here:
   * src/modules/designStudio/components/DesignMenu/DesignCard/index.tsx
   * has had its padding removed so that the product catalog menus can use the full
   * menu space.
   */
  return (
    <system.div height="100%" padding={uiKind === UIKind.LANDSCAPE ? 600 : 400}>
      <SetIntercomVisibility visible={false} />
      <MenuComponent
        closeMenu={closeMenu}
        headerText={headerText}
        requestingOrgName={requestingOrgName}
      />
    </system.div>
  );
};
